/**
 * Formats a number to a given currency standards
 * @param {number} n The number to format
 * @param {currency} currency The the currency type
 * @returns {string} The formatted number
 */
export const formatToCurrency = function (n, currency) {
  switch (currency) {
    case Currency.DKK:
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' kr.'
    case Currency.EURO:
      return '€ ' + n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }
}

export const getCurrencyTypeFromString = function (string) {
  switch (string) {
    case 'DKK':
      return Currency.DKK
    case 'EURO':
      return Currency.EURO
    default:
      return Currency.EURO
  }
}

/**
 * Enum for currency
 * @readonly
 * @enum {symbol}
 */
export const Currency = Object.freeze({
  DKK: Symbol('dkk'),
  EURO: Symbol('euro'),
})

/**
 * Enum for log method
 * @readonly
 * @enum {symbol}
 */
export const LogMethod = Object.freeze({
  Info: Symbol('info'),
  Debug: Symbol('debug'),
  Warning: Symbol('warning'),
  Error: Symbol('error'),
})

/**
 * Enum for NavState state
 * @readonly
 * @enum {symbol}
 */
export const NavState = Object.freeze({
  Open: Symbol('open'),
  Closed: Symbol('closed'),
  Icons: Symbol('icons'),
})

/**
 * Enum for bootstrap variants
 * @readonly
 * @enum {symbol}
 */
export const BootstrapVariant = Object.freeze({
  Primary: Symbol('primary'),
  Secondary: Symbol('secondary'),
  Success: Symbol('success'),
  Warning: Symbol('warning'),
  Danger: Symbol('danger'),
  Info: Symbol('info'),
  Light: Symbol('light'),
  Dark: Symbol('dark'),
})

/**
 * Class for Alerts.
 * @readonly
 * @enum {symbol}
 */
export const Alert = class {
  /**
   * Create an Alert.
   * @param {number} show - When set, shows the alert. Set to a number (seconds) to show and automatically dismiss the alert after the number of seconds has elapsed.
   * @param {boolean} dismissable - When set, enables the dismiss close button.
   * @param {BootstrapVariant} variant - Applies one of the Bootstrap theme color variants to the component.
   * @param {string} message - The message to show in the alert.
   */
  constructor(show, dismissable, variant, message) {
    this.show = show
    this.dismissable = dismissable
    this.variant = variant
    this.message = message
  }
  /**
   * Gets the alert object.
   * @return {object} The alert object.
   */
  get() {
    return {
      show: this.show,
      dismissable: this.dismissable,
      variant: this.variant.description,
      message: this.message,
    }
  }
}

/**
 * Objects of days.
 * @readonly
 */
export const Days = {
  0: 'Monday',
  1: 'Tuesday',
  2: 'Wednesday',
  3: 'Thursday',
  4: 'Friday',
  5: 'Saturday',
  6: 'Sunday',
}

/**
 * Objects of country codes.
 * @readonly
 */
export const CountryCodes = {
  AF: 'Afghanistan',
  AL: 'Albania',
  DZ: 'Algeria',
  AS: 'American Samoa',
  AD: 'Andorra',
  AO: 'Angola',
  AI: 'Anguilla',
  AG: 'Antigua and Barbuda',
  AR: 'Argentina',
  AM: 'Armenia',
  AW: 'Aruba',
  AU: 'Australia',
  AT: 'Austria',
  AZ: 'Azerbaijan',
  BS: 'Bahamas',
  BH: 'Bahrain',
  BD: 'Bangladesh',
  BB: 'Barbados',
  BY: 'Belarus',
  BE: 'Belgium',
  BZ: 'Belize',
  BJ: 'Benin',
  BM: 'Bermuda',
  BT: 'Bhutan',
  BO: 'Bolivia',
  BQ: 'Bonaire',
  BA: 'Bosnia-Herzegovina',
  BW: 'Botswana',
  BV: 'Bouvet Island',
  BR: 'Brazil',
  IO: 'British Indian Ocean Territory',
  BN: 'Brunei Darussalam',
  BG: 'Bulgaria',
  BF: 'Burkina Faso',
  BI: 'Burundi',
  CV: 'Cabo Verde',
  KH: 'Cambodia',
  CM: 'Cameroon',
  CA: 'Canada',
  KY: 'Cayman Islands',
  CF: 'Central African Republic',
  TD: 'Chad',
  CL: 'Chile',
  CN: 'China',
  CX: 'Christmas Island',
  CC: 'Cocos (Keeling) Islands',
  CO: 'Colombia',
  KM: 'Comoros',
  CD: 'Congo',
  CG: 'Congo',
  CK: 'Cook Islands',
  CR: 'Costa Rica',
  HR: 'Croatia',
  CU: 'Cuba',
  CW: 'Curaçao',
  CY: 'Cyprus',
  CZ: 'Czechia',
  CI: "Côte d'Ivoire",
  DK: 'Denmark',
  DJ: 'Djibouti',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  EC: 'Ecuador',
  EG: 'Egypt',
  SV: 'El Salvador',
  GQ: 'Equatorial Guinea',
  ER: 'Eritrea',
  EE: 'Estonia',
  SZ: 'Eswatini',
  ET: 'Ethiopia',
  FK: 'Falkland Islands',
  FO: 'Faroe Islands',
  FJ: 'Fiji',
  FI: 'Finland',
  FR: 'France',
  GF: 'French Guiana',
  PF: 'French Polynesia',
  TF: 'French Southern Territories',
  GA: 'Gabon',
  GM: 'Gambia',
  GE: 'Georgia',
  DE: 'Germany',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GR: 'Greece',
  GL: 'Greenland',
  GD: 'Grenada',
  GP: 'Guadeloupe',
  GU: 'Guam',
  GT: 'Guatemala',
  GG: 'Guernsey',
  GN: 'Guinea',
  GW: 'Guinea-Bissau',
  GY: 'Guyana',
  HT: 'Haiti',
  VA: 'Holy See',
  HN: 'Honduras',
  HK: 'Hong Kong',
  HU: 'Hungary',
  IS: 'Iceland',
  IN: 'India',
  ID: 'Indonesia',
  IR: 'Iran',
  IQ: 'Iraq',
  IE: 'Ireland',
  IM: 'Isle of Man',
  IL: 'Israel',
  IT: 'Italy',
  JM: 'Jamaica',
  JP: 'Japan',
  JE: 'Jersey',
  JO: 'Jordan',
  KZ: 'Kazakhstan',
  KE: 'Kenya',
  KI: 'Kiribati',
  KR: 'Korea',
  KW: 'Kuwait',
  KG: 'Kyrgyzstan',
  LA: "Lao People's Democratic Republic",
  LV: 'Latvia',
  LB: 'Lebanon',
  LS: 'Lesotho',
  LR: 'Liberia',
  LY: 'Libya',
  LI: 'Liechtenstein',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MO: 'Macao',
  MG: 'Madagascar',
  MW: 'Malawi',
  MY: 'Malaysia',
  MV: 'Maldives',
  ML: 'Mali',
  MT: 'Malta',
  MH: 'Marshall Islands',
  MQ: 'Martinique',
  MR: 'Mauritania',
  MU: 'Mauritius',
  YT: 'Mayotte',
  MX: 'Mexico',
  FM: 'Micronesia (Federated States of)',
  MD: 'Moldova (the Republic of)',
  MC: 'Monaco',
  MN: 'Mongolia',
  ME: 'Montenegro',
  MS: 'Montserrat',
  MA: 'Morocco',
  MZ: 'Mozambique',
  MM: 'Myanmar',
  NA: 'Namibia',
  NR: 'Nauru',
  NP: 'Nepal',
  NL: 'Netherlands',
  NC: 'New Caledonia',
  NZ: 'New Zealand',
  NI: 'Nicaragua',
  NE: 'Niger',
  NG: 'Nigeria',
  NU: 'Niue',
  NF: 'Norfolk Island',
  MP: 'Northern Mariana Islands',
  NO: 'Norway',
  OM: 'Oman',
  PK: 'Pakistan',
  PW: 'Palau',
  PS: 'Palestine, State of',
  PA: 'Panama',
  PG: 'Papua New Guinea',
  PY: 'Paraguay',
  PE: 'Peru',
  PH: 'Philippines',
  PN: 'Pitcairn',
  PL: 'Poland',
  PT: 'Portugal',
  PR: 'Puerto Rico',
  QA: 'Qatar',
  MK: 'Republic of North Macedonia',
  RO: 'Romania',
  RU: 'Russian Federation',
  RW: 'Rwanda',
  RE: 'Réunion',
  BL: 'Saint Barthélemy',
  SH: 'Saint Helena, Ascension and Tristan da Cunha',
  KN: 'Saint Kitts and Nevis',
  LC: 'Saint Lucia',
  MF: 'Saint Martin (French part)',
  PM: 'Saint Pierre and Miquelon',
  VC: 'Saint Vincent and the Grenadines',
  WS: 'Samoa',
  SM: 'San Marino',
  ST: 'Sao Tome and Principe',
  SA: 'Saudi Arabia',
  SN: 'Senegal',
  RS: 'Serbia',
  SC: 'Seychelles',
  SL: 'Sierra Leone',
  SG: 'Singapore',
  SX: 'Sint Maarten (Dutch part)',
  SK: 'Slovakia',
  SI: 'Slovenia',
  SB: 'Solomon Islands',
  SO: 'Somalia',
  ZA: 'South Africa',
  GS: 'South Georgia and the South Sandwich Islands',
  SS: 'South Sudan',
  ES: 'Spain',
  LK: 'Sri Lanka',
  SD: 'Sudan',
  SR: 'Suriname',
  SJ: 'Svalbard and Jan Mayen',
  SE: 'Sweden',
  CH: 'Switzerland',
  SY: 'Syrian Arab Republic',
  TW: 'Taiwan',
  TJ: 'Tajikistan',
  TZ: 'Tanzania, United Republic of',
  TH: 'Thailand',
  TL: 'Timor-Leste',
  TG: 'Togo',
  TK: 'Tokelau',
  TO: 'Tonga',
  TT: 'Trinidad and Tobago',
  TN: 'Tunisia',
  TR: 'Turkey',
  TM: 'Turkmenistan',
  TC: 'Turks and Caicos Islands',
  TV: 'Tuvalu',
  UG: 'Uganda',
  UA: 'Ukraine',
  AE: 'United Arab Emirates',
  GB: 'United Kingdom',
  UM: 'United States Minor Outlying Islands',
  US: 'United States of America',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VU: 'Vanuatu',
  VE: 'Venezuela (Bolivarian Republic of)',
  VN: 'Vietnam',
  VG: 'Virgin Islands (British)',
  VI: 'Virgin Islands (U.S.)',
  WF: 'Wallis and Futuna',
  EH: 'Western Sahara',
  YE: 'Yemen',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
  AX: 'Åland Islands',
}

/**
 * List of country options, formatted to work with the b-form-select component from bootstrap-vue
 * @returns {[{}]} The list of country options
 */
export const Countries = Object.keys(CountryCodes).map((key) => {
  return {
    value: key,
    text: CountryCodes[key],
  }
})

/**
 * List of cardinal options, formatted to work with the b-form-select component from bootstrap-vue
 * @returns {[{}]} The list of cardinal options
 */
export const Cardinals = [
  { value: 'N', text: 'North' },
  { value: 'S', text: 'South' },
  { value: 'E', text: 'East' },
  { value: 'W', text: 'West' },
]

/**
 * Get cardinal name from cardinal point.
 * @param {string} cardinal The cardinal point
 * @returns {string} The cardinal name.
 */
export const GetCardinals = function (cardinal) {
  let cardinals = {
    N: 'North',
    S: 'South',
    E: 'East',
    W: 'West',
  }
  return cardinals[cardinal] || ''
}

/**
 * Returns the corresponding country name for the given code
 * @param {string} countryCode The Alpha-2 code
 * @returns {string} The country name
 */
export const getCountryByCode = function (code) {
  const country = Countries.find((element) => element.value == code)
  return country ? country.text : ''
}

/**
 * Enum for chart type
 * @readonly
 * @enum {symbol}
 */
export const ChartType = Object.freeze({
  Bar: Symbol('Bar'),
  Pie: Symbol('Pie'),
  Line: Symbol('Line'),
})

/**
 * Enum for exporting type
 * @readonly
 * @enum {symbol}
 */
export const ExportingType = {
  JSON: Symbol('JSON'),
  CSV: Symbol('CSV'),
  PRINT: Symbol('PRINT'),
  TEMPLATE: Symbol('TEMPLATE'),
}

/**
 * Enum for table action
 * @readonly
 * @enum {symbol}
 */
export const TableAction = {
  Adding: Symbol('adding'),
  Editing: Symbol('editing'),
  Copying: Symbol('copying'),
  Deleting: Symbol('deleting'),
  Importing: Symbol('importing'),
  Exporting: Symbol('exporting'),
  Printing: Symbol('printing'),
}

/**
 *
 * @param {Timestamp} timestamp
 * @returns
 */
export const timestampToDate = function (timestamp) {
  if (timestamp) {
    const date = new Date(timestamp.seconds * 1000)
    const day = date.getUTCDate()
    let month = date.getUTCMonth() + 1
    month = month >= 10 ? month : '0' + month
    const year = date.getUTCFullYear()
    return `${day}.${month}.${year}`
  } else {
    return ''
  }
}

/**
 * Returns a given date to string, in DD-MM-YYYY format.
 * @param {Date} date
 */
export const dateToString = function (date) {
  let day, month, year

  day = date.getDate()
  month = date.getMonth() + 1
  year = date.getFullYear()

  day = day.toString().padStart(2, '0')

  month = month.toString().padStart(2, '0')

  return `${day}-${month}-${year}`
}

import { getApp } from 'firebase/app'
import { doc, getFirestore } from 'firebase/firestore'
/**
 * Gets a DocumentReference instance that refers to the document at the specified absolute path.
 * @param app — The firebase app to read from.
 * @param path — The path of the document.
 * @returns The DocumentReference (if it exists).
 * @note We need this because the store actions are async, and we need a sync version.
 */
export const getDocumentReference = function ({ app, path }) {
  return doc(getFirestore(getApp(app || 'secondary')), path)
}

/**
 * Gets a value of an object, based on a key, as string.
 * @param {string} o - The object.
 * @param {string} s - The key, as string.
 * @returns
 */
export const byString = function (o, s) {
  s = s.replace(/\[(\w+)\]/g, '.$1')
  s = s.replace(/^\./, '')
  var a = s.split('.')
  for (var i = 0, n = a.length; i < n; ++i) {
    var k = a[i]
    if (k in o) {
      o = o[k]
    } else {
      return
    }
  }
  return o
}

/**
 * Returns a concatenated string, of all the values in an array of object, refered to buy key.
 * @param {array} values The array of objects.
 * @param {string} key The key to concatenate.
 */
export const concatArrayValueByKey = function (values, key) {
  console.log('concatArrayValueByKey', values, key)
  let array = []
  values.forEach((value) => {
    array.push(value[key])
  })
  return array.join(',')
}

/**
 * Ellips a string.
 * @param {string} string The string to ellips.
 * @returns The ellipsed string.
 */
export const getEllipsis = function (string) {
  return string.length > 60 ? string.substring(0, 59) + '..' : string
}

/**
 * Downloads a CSV template for a given document.
 * @param {object} blueprint The blueprint to download a template for.
 */
export const documentTemplate = function (blueprint) {
  let csv = []
  blueprint.form.tabs.forEach((tab) => {
    tab.elements.forEach((element) => {
      csv.push(element.key)
    })
  })
  csv = csv.join(',')
  var dataStr = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
  var downloadAnchorNode = document.createElement('a')
  downloadAnchorNode.setAttribute('href', dataStr)
  downloadAnchorNode.setAttribute('download', `${blueprint.collection}-template-${dateToString(new Date())}.csv`)
  document.body.appendChild(downloadAnchorNode)
  downloadAnchorNode.click()
  downloadAnchorNode.remove()
}

/**
 * Downloads a JSON file of the given documents.
 * @param {array} documents The documents to export.
 * @param {object} blueprint The blueprint the documents belong to.
 */
export const documentsToJSON = function (documents, blueprint) {
  var dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(documents))
  var downloadAnchorNode = document.createElement('a')
  downloadAnchorNode.setAttribute('href', dataStr)
  downloadAnchorNode.setAttribute('download', `${blueprint.collection}-${dateToString(new Date())}.json`)
  document.body.appendChild(downloadAnchorNode)
  downloadAnchorNode.click()
  downloadAnchorNode.remove()
}

/**
 * Tempalte for the function "toPrint".
 * Will be removed in the future.
 */
const template = `
<link href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" rel="stylesheet"
integrity="sha384-1BmE4kWBq78iYhFldvKuhfTAU6auU8tT94WrHftjDbrCEXSU1oBoqyl2QvZ6jIW3" crossorigin="anonymous">
<script src="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/js/bootstrap.bundle.min.js"
integrity="sha384-ka7Sk0Gln4gmtz2MlQnikT1wXgYsOg+OMhuP+IlRH9sENBO0LRn5q+8nbTov4+1p"
crossorigin="anonymous"></script>
<style>
@media print {
    @page {
        margin: 0;
    }
    body {
        margin: 1.6cm;
    }
}
</style>
<svg xmlns="http://www.w3.org/2000/svg" width="85.322" height="20.539" viewBox="0 0 85.322 20.539">
  <g id="Group_388" data-name="Group 388" transform="translate(-68.863 -37.097)">
      <g id="Group_142" data-name="Group 142" transform="translate(68.863 37.097)">
          <path id="Path_42" data-name="Path 42"
              d="M319.711,253.346a4.91,4.91,0,0,1,.5.189.674.674,0,0,1,.405.657c-.009.841,0,1.682,0,2.524v16.119c0,.9-.721,1.307-1.575.881a1.3,1.3,0,0,1-.42-.328q-4.522-5.55-9.033-11.108-1.875-2.307-3.752-4.612c-.038-.047-.082-.091-.164-.181v.333q0,7.56,0,15.119a.8.8,0,0,1-.706.872,1.139,1.139,0,0,1-1.224-.439.992.992,0,0,1-.072-.439q-.005-5.881,0-11.762c0-2.3.007-4.6-.007-6.9a.774.774,0,0,1,.514-.764,1.2,1.2,0,0,1,1.443.273q3.07,3.835,6.137,7.67l6.73,8.409c.024.031.052.06.126.144V254.342c0-.592.193-.819.81-.954a.416.416,0,0,0,.087-.041Z"
              transform="translate(-258.122 -253.346)" fill="#233043"></path>
          <path id="Path_43" data-name="Path 43"
              d="M96.061,253.346c.4.066.8.122,1.2.2a6.037,6.037,0,0,1,5.066,5.266,6.374,6.374,0,0,1-3.72,6.489c-.074.036-.147.074-.245.123.364.445.721.881,1.08,1.317q2.295,2.794,4.591,5.586a1.16,1.16,0,0,1,.289.743.749.749,0,0,1-.638.775,1.2,1.2,0,0,1-1.212-.382q-2.945-3.608-5.9-7.213a.568.568,0,0,0-.5-.236c-1.862.009-3.724.005-5.585.005h-.339v6.862a.992.992,0,0,1-1.361.951,1.107,1.107,0,0,1-.7-.532V253.87a1.021,1.021,0,0,1,.551-.524Zm-5.894,10.862h.313c1.8,0,3.591-.019,5.386.011a3.376,3.376,0,0,0,1.642-.4,4.667,4.667,0,0,0,2.715-4.95,4.1,4.1,0,0,0-1.648-2.687,4.825,4.825,0,0,0-2.845-.827c-1.819-.027-3.639-.016-5.458-.02a.546.546,0,0,0-.1.023Z"
              transform="translate(-88.093 -253.346)" fill="#233043"></path>
          <path id="Path_44" data-name="Path 44"
              d="M237.1,253.346a1,1,0,0,1,.556.571q3.1,6.681,6.223,13.355,1.291,2.768,2.58,5.538a1.087,1.087,0,0,1,.1.363.614.614,0,0,1-.343.639.622.622,0,0,1-.715-.12c-.773-.654-1.552-1.3-2.305-1.974a48.611,48.611,0,0,1-4.155-4.086c-.294-.334-.559-.691-.844-1.033a4.858,4.858,0,0,1-.986-2.581c-.214-1.635-.414-3.272-.556-4.914s-.2-3.307-.308-4.96a.75.75,0,0,1,.4-.8Z"
              transform="translate(-205.024 -253.346)" fill="#233043"></path>
          <path id="Path_45" data-name="Path 45"
              d="M192.073,253.346a.793.793,0,0,1,.393.827c-.124,1.9-.214,3.8-.361,5.695a32.715,32.715,0,0,1-.727,5.2,5.314,5.314,0,0,1-1.219,2.1,39.9,39.9,0,0,1-4.058,4.1c-.889.8-1.806,1.576-2.718,2.354-.331.283-.631.314-.877.135a.726.726,0,0,1-.173-.911q1.3-2.79,2.6-5.579,2.216-4.755,4.431-9.51c.593-1.274,1.191-2.546,1.777-3.823a1.157,1.157,0,0,1,.529-.594Z"
              transform="translate(-162.356 -253.346)" fill="#233043"></path>
          <path id="Path_46" data-name="Path 46" d="M88.644,253.346a1.021,1.021,0,0,0-.551.524v-.524Z"
              transform="translate(-88.093 -253.346)" fill="#233043"></path>
          <path id="Path_47" data-name="Path 47"
              d="M410.961,263.766q0-4.677,0-9.354c0-.582.289-.857.9-.857,2.029,0,4.059-.037,6.086.013a9.913,9.913,0,0,1,9.782,7.512,10.444,10.444,0,0,1-.842,7.55,9.629,9.629,0,0,1-6.906,5.09,13.522,13.522,0,0,1-2.481.244c-1.894.035-3.79.011-5.685.011a.754.754,0,0,1-.852-.807Q410.96,268.467,410.961,263.766Zm2.053,8.331h.31c1.32,0,2.64.017,3.959-.007a15.006,15.006,0,0,0,2.166-.158,7.111,7.111,0,0,0,6.038-4.977,10.4,10.4,0,0,0,.289-5.2,7.509,7.509,0,0,0-1.7-3.679,7.823,7.823,0,0,0-5.889-2.612c-1.667-.064-3.338-.017-5.008-.017a1.1,1.1,0,0,0-.162.023Z"
              transform="translate(-342.753 -253.502)" fill="#233043"></path>
      </g>
  </g>
</svg>
<h1>Configuration Request</h1>
<div>
  <h3>Details</h3>
  <div class="container-fluid">
    <div class="row">
      <div class="col-6">
        Date
      </div>
      <div class="col-6">
        <%= document._onCreateDate.toDate().toLocaleDateString(); %>
      </div>
      <div class="col-6">
        Product
      </div>
      <div class="col-6">
        <%= document.configuration.product.name %>
      </div>
    </div>
  </div>
  </div>
</div>
<div>
  <h3>Contact</h3>
  <div> <%= document.firstname + " " + document.lastname %> </div>
  <a href="mailto:<%= document.email %>"> <%= document.email %> </a>
  <br/>
  <a href="tel:<%= document.phone %>"> <%= document.phone %> </a>
  <div class="d-flex">
    <div class="me-2"> <%= document.country %> </div>
    <div class="me-2"> <%= document.zipCode %> </div>
    <div> <%= document.city %> </div>
    <div> <%= document.contact_method %> </div>
  </div>
</div>
<table class="table">
    <thead>
        <tr>
            <th scope="col">Name</th>
            <th scope="col">Price</th>
        </tr>
    </thead>
    <tbody>
        <% for(var i=0; i<document.configuration.options.length; i++) {%>
            <tr>
                <td>
                    <%= document.configuration.options[i]?.name %>
                </td>
                <td>
                    <%= document.configuration.options[i]?.price %>
                </td>
            </tr>
            <% } %>
    </tbody>
</table>
`

/**
 * Opens a print dialog, for the given document(s).
 * TODO - Currently only works for 1 documents.
 * @param {Documents} documents
 */
export const toPRINT = function (document) {
  //TODO implement
  let ejs = require('ejs')
  let WinPrint = window.open('', '', '')
  let html = ejs.render(template, { document: document })
  WinPrint.document.write(html)
  WinPrint.document.close()
  WinPrint.focus()
  setTimeout(() => {
    // why?
    WinPrint.print()
  }, 500)
}

/**
 * Downloads a CSV file of the given documents.
 * @param {array} documents The documents to export.
 * @param {object} blueprint The blueprint the documents belong to.
 * ??
 */
export const documentToCSV = function (documents, blueprint) {
  console.log('documents', documents)
  console.log('blueprint', blueprint)
  var json = documents
  // var fields = Object.keys(json[0])
  // var replacer = function (key, value) {
  //   return value === null ? '' : value
  // }
  var fields = Object.keys(documents[0])
  console.log('fields', fields)
  var replacer = function (key, value) {
    return value === null ? '' : value
  }
  var csv = json.map(function (row) {
    return fields
      .map(function (fieldName) {
        return JSON.stringify(row[fieldName], replacer)
      })
      .join(',')
  })
  csv.unshift(fields.join(','))
  csv = csv.join('\r\n')
  var dataStr = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
  var downloadAnchorNode = document.createElement('a')
  downloadAnchorNode.setAttribute('href', dataStr)
  downloadAnchorNode.setAttribute('download', `${blueprint.collection}-${dateToString(new Date())}.csv`)
  document.body.appendChild(downloadAnchorNode)
  downloadAnchorNode.click()
  downloadAnchorNode.remove()
}

/**
 * Parses a CSV file to a JSON object, using the given blueprint.
 * @param {string} csv The CSV file to parse.
 * @param {object} blueprint The blueprint to parse the file using.
 * @returns
 */
export const csvToJSON = function (csv, blueprint) {
  var lines = csv.split('\r\n')
  var documents = []
  var headers = lines[0].split(',')

  for (var i = 1; i < lines.length; i++) {
    var document = {}
    var currentLine = lines[i].split(',')
    blueprint.form.tabs.forEach((tab) => {
      tab.elements.forEach((element) => {
        let index = headers.indexOf(element.key)
        if (index != -1) {
          switch (element.type) {
            case 'bool':
              document[element.key] = currentLine[index] === 'TRUE'
              break
            case 'number':
              document[element.key] = currentLine[index] ? parseInt(currentLine[index]) : element.default
              break
            // case 'select':
            //   document[element.key] = doc(getFirestore(getApp('secondary')), currentLine[index])
            //   break
            // case 'multiselect':
            //   document[element.key] = doc(getFirestore(getApp('secondary')), currentLine[index])
            //   break
            default:
              document[element.key] = currentLine[index]
              break
          }
        } else {
          document[element.key] = element.default
        }
      })
    })
    documents.push(document)
  }
  return documents
}

/**
 * Checks if a date is today or not.
 * @param {object} date The date to check for.
 * @returns True if the date is today, false if not.
 */
export const isToday = function (date) {
  const today = new Date()
  return date.getDate() == today.getDate() && date.getMonth() == today.getMonth() && date.getFullYear() == today.getFullYear()
}
/**
 * Converts a plural word to a singular variant.
 * @param {string} plural The plural word
 * @returns The singular variant.
 */
export const pluralToSingular = function (plural) {
  return plural.slice(0, -1)
}
